import React, { ReactNode, useEffect, useRef } from 'react'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import type { ReactPortal } from 'react'
import ReactDOM from 'react-dom'

export type ModalProps = {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  className?: string
  header?: ReactNode
  footer?: ReactNode
  showCloseButton?: boolean
  size?: 'default' | 'full' | 'medium' | 'small'
  ariaLabel?: string
  backdropClickCloses?: boolean
  testId?: string
}

const MODAL_ROOT_ID = 'modal-root'

export function Modal(props: ModalProps): ReactPortal | null {
  const {
    isOpen,
    onClose,
    children,
    className = '',
    header,
    footer,
    showCloseButton = true,
    size = 'default',
    ariaLabel = 'Dialog',
    backdropClickCloses = true,
    testId
  } = props
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  useFocusTrap(isOpen, dialogRef)

  if (!isOpen) return null

  let modalRoot = document.getElementById(MODAL_ROOT_ID)
  if (!modalRoot) {
    modalRoot = document.createElement('div')
    modalRoot.id = MODAL_ROOT_ID
    document.body.appendChild(modalRoot)
  }

  // Size modifier classes
  let sizeClass = ''
  if (size === 'full') sizeClass = 'Modal--fullScreen'
  else if (size === 'medium') sizeClass = 'Modal--mediumWidth'
  else if (size === 'small') sizeClass = 'Modal--smallWidth'

  // Backdrop click handler
  const handleBackdropClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (backdropClickCloses && e.target === e.currentTarget) {
      onClose()
    }
  }

  return ReactDOM.createPortal(
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={`Modal ${sizeClass} ${className}`.trim()}
      onClick={handleBackdropClick}
      tabIndex={-1}
      data-testid={testId}
    >
      <dialog
        className="Modal-window"
        open
        aria-modal="true"
        aria-label={ariaLabel}
        ref={dialogRef}
        tabIndex={-1}
      >
        {header && (
          <header
            className="Modal-header"
            data-testid={testId ? `${testId}-header` : undefined}
          >
            {header}
          </header>
        )}
        {showCloseButton && (
          <button
            className="Button Button--icon Button--icon--neutral Modal-icon-button--close"
            onClick={onClose}
            aria-label="Close this dialog"
            style={{ position: 'absolute', top: 8, right: 8 }}
          >
            <span className="u-hiddenVisually">Close this dialog</span>
            <span aria-hidden="true">close</span>
          </button>
        )}
        <div
          className="Modal-content Content"
          data-testid={testId ? `${testId}-content` : undefined}
        >
          {children}
        </div>
        {footer && (
          <footer
            className="Modal-footer"
            data-testid={testId ? `${testId}-footer` : undefined}
          >
            {footer}
          </footer>
        )}
      </dialog>
    </div>,
    modalRoot
  )
}
